<template>
  <div>
    <MglMap :accessToken="accessToken" :mapStyle.sync="mapStyle" class="map" @load="handleLoaded" />
  </div>
</template>

<script>
import 'mapbox-gl/dist/mapbox-gl.css'
import Vue from 'vue'
import Mapbox from 'mapbox-gl'
import { MglMap } from 'vue-mapbox'
import turfGreatCircle from '@turf/great-circle'
import { get, sync } from 'vuex-pathify'
import { getAirport, getPlaceCoordinates } from '@/services/api'
import MapPopup from '@/components/MapPopup'

const MapPopupClass = Vue.extend(MapPopup)

export default {
  components: { MglMap, MapPopup },
  data () {
    return {
      accessToken: process.env.MIX_MAPBOX_ACCESS_TOKEN,
      mapStyle: process.env.MIX_MAPBOX_STYLE_URL,
    }
  },

  computed: {
    ...get('explorer/', ['places', 'selectedPlace', 'firstSuggestedAirport']),
    ...sync('explorer/', ['fromAirport', 'toAirport']),
  },

  watch: {
    async selectedPlace (val) {
      if (!val) {
        return
      }
      const { data: { longitude, latitude } } = await getPlaceCoordinates(val)
      if (this.firstSuggestedAirport) {
        const { coordinates: { longitude: airportLongitude, latitude: airportLatitude } } = this.firstSuggestedAirport
        this.map.fitBounds([[longitude, latitude], [airportLongitude, airportLatitude]], { padding: 140, duration: 2000 })
      } else {
        this.map.flyTo({ center: [longitude, latitude], zoom: 8 })
      }
    },
    fromAirport: {
      handler (val) {
        if (!this.map) return
        if (!val) {
          this.removeLine()
          return this.setAirportPoint('from', null)
        }

        this.setAirportPoint('from', [val.longitude, val.latitude])

        if (this.toAirport) this.bothAirportsSelectedHandler()
        else this.flyToAirport(val)
      }
    },
    toAirport: {
      handler (val) {
        if (!this.map) return
        if (!val) {
          this.removeLine()
          return this.setAirportPoint('to', null)
        }

        this.setAirportPoint('to', [val.longitude, val.latitude])

        if (this.fromAirport) this.bothAirportsSelectedHandler()
        else this.flyToAirport(val)
      }
    }
  },

  methods: {
    async handleLoaded ({ map, component }) {
      this.map = map

      this.popup = new Mapbox.Popup({
        closeButton: false,
        closeOnClick: false,
        offset: 32,
        maxWidth: '280px',
      })

      // Airports source
      this.map.addSource('airports', {
        type: 'geojson',
        data: `${process.env.MIX_API_URL}/fractional/airports.geojson`,
        cluster: true,
        clusterMaxZoom: 6,
        clusterRadius: 56,
      })

      this.map.addLayer({
        id: 'airport-cluster',
        type: 'circle',
        source: 'airports',
        filter: ['has', 'point_count'],
        paint: {
          'circle-color': '#fff',
          'circle-radius': 14,
        }
      })

      this.map.addLayer({
        id: 'airport-cluster-count',
        type: 'symbol',
        source: 'airports',
        filter: ['has', 'point_count'],
        layout: {
          'text-field': '{point_count_abbreviated}',
          'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
          'text-size': 12
        }
      })

      this.map.addLayer({
        id: 'airport',
        type: 'circle',
        source: 'airports',
        filter: ['!', ['has', 'point_count']],
        minzoom: 4,
        paint: {
          'circle-color': '#979797',
          'circle-radius': 6,
          'circle-stroke-width': 10,
          'circle-stroke-color': '#fff'
        }
      })

      if (this.fromAirport && this.toAirport) {
        this.bothAirportsSelectedHandler()
        this.setAirportPoint('from', [this.fromAirport.coordinates.longitude, this.fromAirport.coordinats.latitude])
        this.setAirportPoint('to', [this.toAirport.coordinates.longitude, this.toAirport.coordinates.latitude])
      } else if (this.fromAirport || this.toAirport) {
        this.flyToAirport(this.fromAirport || this.toAirport)
      }

      // Click airport-cluster
      map.on('click', 'airport-cluster', (e) => this.handleClusterClick(e))

      // Click airport
      map.on('click', 'airport', (e) => this.handleAirportClick(e))

      // Hover over airport cluster
      map.on('mouseenter', 'airport-cluster', () => this.setMapCursor('pointer'))
      map.on('mouseleave', 'airport-cluster', () => this.setMapCursor())

      // Hover over airport
      map.on('mouseenter', 'airport', (e) => this.handleAirportMouseover(e.features[0]))
      map.on('mouseleave', 'airport', () => this.handleAirportMouseleave())
    },
    flyToAirport (airport) {
      const coordinates = [airport.coordinates.longitude, airport.coordinates.latitude]
      this.map.flyTo({ center: coordinates, zoom: 8, duration: 2000 })
    },
    handleClusterClick (e) {
      const [feature] = this.map.queryRenderedFeatures(e.point, { layers: ['airport-cluster'] })
      this.map.getSource('airports').getClusterExpansionZoom(feature.properties.cluster_id, (err, zoom) => {
        if (err) return
        this.map.easeTo({ center: feature.geometry.coordinates, zoom })
      })
    },
    async handleAirportClick (e) {
      const [feature] = this.map.queryRenderedFeatures(e.point, { layers: ['airport'] })
      if (!feature.properties.operable) return
      const { data } = await getAirport(feature.properties.id)
      if (!this.fromAirport) this.fromAirport = data
      else this.toAirport = data
    },
    handleAirportMouseover (feature) {
      if (!feature.properties.operable) this.setMapCursor('not-allowed')
      else this.setMapCursor('pointer')
      this.popup.setLngLat(feature.geometry.coordinates).setHTML('<div id="popup"></div>').addTo(this.map)
      const popupInstance = new MapPopupClass({
        propsData: { id: feature.properties.id }
      })
      popupInstance.$mount('#popup')
      this.popup._update()
    },
    handleAirportMouseleave () {
      this.setMapCursor()
      this.popup.remove()
    },
    setMapCursor (payload) {
      this.map.getCanvas().style.cursor = payload || ''
    },
    setAirportPoint (airportType, coordinates = null) {
      if (this.map.getSource(`${airportType}Airport`)) {
        this.map.removeLayer(`${airportType}-airport`)
        this.map.removeSource(`${airportType}Airport`)
      }

      if (coordinates) {
        this.map.addSource(`${airportType}Airport`, {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features: [ { type: 'Feature', geometry: { type: 'Point', coordinates } } ]
          }
        })

        this.map.addLayer({
          id: `${airportType}-airport`,
          source: `${airportType}Airport`,
          type: 'circle',
          paint: { 'circle-color': '#80C190', 'circle-radius': 6, 'circle-stroke-width': 10, 'circle-stroke-color': '#fff' }
        })
      }
    },

    removeLine () {
      if (this.map.getSource('route')) {
        this.map.removeLayer('route')
        this.map.removeSource('route')
      }
    },

    bothAirportsSelectedHandler () {
      const fromCoordinates = [this.fromAirport.coordinates.longitude, this.fromAirport.coordinates.latitude]
      const toCoordinates = [this.toAirport.coordinates.longitude, this.toAirport.coordinates.latitude]

      this.removeLine()

      this.map.fitBounds([fromCoordinates, toCoordinates], { padding: 140, duration: 2000 })

      const route = {
        type: 'FeatureCollection',
        features: [],
      }

      route.features[0] = turfGreatCircle(fromCoordinates, toCoordinates)

      this.map.addSource('route', { type: 'geojson', data: route })

      this.map.addLayer({
        id: 'route',
        source: 'route',
        type: 'line',
        paint: {
          'line-width': 2,
          'line-color': '#999382',
          'line-dasharray': [2, 1]
        }
      })

      this.setAirportPoint('from', fromCoordinates)
      this.setAirportPoint('to', toCoordinates)
    }
  }
}
</script>

<style scoped>
.map >>> canvas:focus {
  @apply outline-none;
}

.map >>> .mapboxgl-popup-tip {
  display: none;
}

.map >>> .mapboxgl-popup-content {
  padding: 0;
  background: none;
  box-shadow: none;
}

</style>
